import React, {useEffect, useState} from 'react';
import {
  Image,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import SlideModal from '@/Atoms/SlideModal';
import CCText from '@/Atoms/CCText';
import Box from '@/Atoms/Box';
import Spacer from '@/Atoms/Spacer';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import {
  AnswerBlock,
  FaqItem,
  FaqSection,
  HelpModalDirectLink,
} from './types';

type Props = {
  sections: FaqSection[];
  directLink?: HelpModalDirectLink;
  buttonLabel?: string;
  modalTitle?: string;
};

function HelpModal({
  sections,
  directLink,
  buttonLabel = 'Help',
  modalTitle = 'How can we help?',
}: Props) {
  const [open, setOpen] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  const activeSection = sections.find(s => s.id === activeSectionId) || null;

  const handleClose = () => {
    setOpen(false);
    setActiveSectionId(null);
  };

  const handleBack = () => setActiveSectionId(null);

  const headerTitle = (
    <Box flexDirection="row" alignItems="center">
      {activeSection && (
        <TouchableOpacity onPress={handleBack} style={styles.backButton}>
          <Ionicons
            name="arrow-back"
            size={20}
            color={CCColors.Primary.Dark}
          />
        </TouchableOpacity>
      )}
      <CCText fontSize={FontSizes[16]} fontWeight="semibold">
        {activeSection ? activeSection.title : modalTitle}
      </CCText>
    </Box>
  );

  return (
    <>
      <Pressable
        onPress={() => setOpen(true)}
        style={styles.floatingButton}
        accessibilityLabel="Open help">
        <View style={styles.floatingButtonInner}>
          <CCText
            fontSize={FontSizes[20]}
            fontWeight="bold"
            color={CCColors.Grey.Grey}
            style={styles.floatingButtonSymbol}>
            ?
          </CCText>
          <CCText
            fontSize={FontSizes[9]}
            color={CCColors.Grey.Grey}
            fontWeight="semibold"
            style={styles.floatingButtonLabel}>
            {buttonLabel}
          </CCText>
        </View>
      </Pressable>

      <SlideModal
        type="side"
        visible={open}
        onClose={handleClose}
        modalHeaderTitle={headerTitle}
        contentPadding={0}>
        <View style={styles.modalBody}>
          <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
            {activeSection ? (
              <SectionDetail section={activeSection} />
            ) : (
              <SectionList
                sections={sections}
                onSelect={id => setActiveSectionId(id)}
              />
            )}
          </ScrollView>

          {directLink && (
            <View style={styles.directLinkBar}>
              <CCText fontSize={FontSizes[13]}>
                If you're still facing issues, 'Raise an Issue' and continue
                the class using this{' '}
                <CCText
                  fontSize={FontSizes[13]}
                  fontWeight="bold"
                  color={'#1E66F5'}
                  onPress={() => Linking.openURL(directLink.url)}>
                  zoom link
                </CCText>
                .
              </CCText>
              <Spacer spacing={6} />
              <CCText
                fontSize={FontSizes[12]}
                color={CCColors.Grey.Grey}>
                Note: Classroom features won’t be available in the zoom link.
              </CCText>
            </View>
          )}
        </View>
      </SlideModal>
    </>
  );
}

type SectionListProps = {
  sections: FaqSection[];
  onSelect: (id: string) => void;
};

function SectionList({sections, onSelect}: SectionListProps) {
  return (
    <View>
      {sections.map(section => (
        <TouchableOpacity
          key={section.id}
          onPress={() => onSelect(section.id)}
          style={styles.sectionCard}>
          <Box flexDirection="row" alignItems="center">
            {section.emoji && (
              <>
                <CCText fontSize={FontSizes[20]}>{section.emoji}</CCText>
                <Spacer spacing={12} horizontal />
              </>
            )}
            <View style={styles.sectionCardText}>
              <CCText fontSize={FontSizes[15]} fontWeight="semibold">
                {section.title}
              </CCText>
              <Spacer spacing={2} />
              <CCText fontSize={FontSizes[11]} color={CCColors.Grey.Grey}>
                {section.items.length}{' '}
                {section.items.length === 1 ? 'topic' : 'topics'}
              </CCText>
            </View>
            <Ionicons
              name="chevron-forward"
              size={18}
              color={CCColors.Grey.Grey}
            />
          </Box>
        </TouchableOpacity>
      ))}
    </View>
  );
}

function SectionDetail({section}: {section: FaqSection}) {
  const [openItemId, setOpenItemId] = useState<string | null>(null);

  return (
    <View>
      {section.items.map(item => {
        const isOpen = item.id === openItemId;
        return (
          <View key={item.id} style={styles.itemCard}>
            <TouchableOpacity
              onPress={() => setOpenItemId(isOpen ? null : item.id)}
              style={styles.itemHeader}>
              <View style={styles.itemHeaderText}>
                <CCText fontSize={FontSizes[14]} fontWeight="semibold">
                  {item.question}
                </CCText>
              </View>
              <Ionicons
                name={isOpen ? 'chevron-up' : 'chevron-down'}
                size={18}
                color={CCColors.Grey.Grey}
              />
            </TouchableOpacity>
            {isOpen && (
              <View style={styles.itemBody}>
                {item.blocks.map((block, idx) => (
                  <BlockRenderer key={idx} block={block} />
                ))}
              </View>
            )}
          </View>
        );
      })}
    </View>
  );
}

function AutoSizedImage({
  src,
  alt,
}: {
  src: AnswerBlock extends {type: 'image'; src: infer S} ? S : never;
  alt: string;
}) {
  const source = typeof src === 'string' ? {uri: src} : src;
  const [aspect, setAspect] = useState<number | null>(() => {
    if (typeof src !== 'string') {
      const resolved = Image.resolveAssetSource(source as any);
      if (resolved?.width && resolved?.height) {
        return resolved.width / resolved.height;
      }
    }
    return null;
  });

  useEffect(() => {
    if (aspect !== null) return;
    if (typeof src === 'string') {
      Image.getSize(
        src,
        (w, h) => h > 0 && setAspect(w / h),
        () => setAspect(1),
      );
    }
  }, [src, aspect]);

  return (
    <Image
      source={source}
      style={[styles.image, aspect ? {aspectRatio: aspect} : styles.imageFallback]}
      resizeMode="contain"
      accessibilityLabel={alt}
    />
  );
}

function BlockRenderer({block}: {block: AnswerBlock}) {
  switch (block.type) {
    case 'paragraph':
      return (
        <View style={styles.block}>
          <CCText fontSize={FontSizes[13]}>{block.text}</CCText>
        </View>
      );
    case 'list':
      return (
        <View style={styles.block}>
          {block.items.map((line, i) => (
            <Box key={i} flexDirection="row" style={styles.listRow}>
              <CCText fontSize={FontSizes[13]} color={CCColors.Grey.Grey}>
                {block.ordered ? `${i + 1}.` : '•'}
              </CCText>
              <Spacer spacing={8} horizontal />
              <View style={styles.listText}>
                <CCText fontSize={FontSizes[13]}>{line}</CCText>
              </View>
            </Box>
          ))}
        </View>
      );
    case 'image': {
      return (
        <View style={styles.block}>
          <AutoSizedImage src={block.src} alt={block.alt} />
          {block.caption && (
            <>
              <Spacer spacing={4} />
              <CCText
                fontSize={FontSizes[11]}
                color={CCColors.Grey.Grey}
                textAlign="center">
                {block.caption}
              </CCText>
            </>
          )}
        </View>
      );
    }
    case 'link':
      return (
        <View style={styles.block}>
          <TouchableOpacity onPress={() => Linking.openURL(block.url)}>
            <CCText
              fontSize={FontSizes[13]}
              color={CCColors.Primary.Default}
              fontWeight="semibold">
              {block.label} ↗
            </CCText>
          </TouchableOpacity>
        </View>
      );
  }
}

const FLOATING_SIZE = 60;

const styles = StyleSheet.create({
  floatingButton: {
    position: 'fixed' as any,
    bottom: 20,
    right: 20,
    zIndex: 1000,
    alignItems: 'center',
  },
  floatingButtonInner: {
    width: FLOATING_SIZE,
    height: FLOATING_SIZE,
    borderRadius: FLOATING_SIZE / 2,
    borderWidth: 3,
    borderColor: CCColors.Orange,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 4,
  },
  floatingButtonSymbol: {
    lineHeight: 20,
  },
  floatingButtonLabel: {
    marginTop: 0,
    lineHeight: 11,
  },
  backButton: {
    marginRight: 8,
    padding: 4,
  },
  modalBody: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 24,
  },
  sectionCard: {
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#FAFAFA',
    marginBottom: 10,
  },
  sectionCardText: {
    flex: 1,
  },
  itemCard: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
    marginBottom: 10,
    overflow: 'hidden',
  },
  itemHeader: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FAFAFA',
  },
  itemHeaderText: {
    flex: 1,
    paddingRight: 8,
  },
  itemBody: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderTopWidth: 1,
    borderTopColor: '#EEF0F3',
  },
  block: {
    marginBottom: 8,
  },
  listRow: {
    marginBottom: 4,
    alignItems: 'flex-start',
  },
  listText: {
    flex: 1,
  },
  image: {
    width: '100%',
    maxHeight: 480,
    borderRadius: 8,
    backgroundColor: '#F3F4F6',
    alignSelf: 'center',
  },
  imageFallback: {
    height: 220,
  },
  directLinkBar: {
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
  },
});

export default HelpModal;
export type {FaqSection, FaqItem, AnswerBlock, HelpModalDirectLink};
